import type { AxiosRequestConfig } from 'axios';
import { apiClient } from './client';
import { API_URL } from '@/lib/apiUrl';
import type { LoginCredentials, RegisterData } from '@/types';

/**
 * Cookie-auth endpoints (Wave 2.4). These set httpOnly session/refresh cookies +
 * a JS-readable CSRF cookie on the response and return only `{ ok: true }`. The
 * builder never sees the token — it hydrates identity from GET /auth/me afterwards
 * (see useAuthStore.hydrate).
 */

/**
 * Two-step sign-in: when the account has it on, the password alone answers with a challenge (no
 * cookies). The code from the authenticator app, a text, an email or a backup code finishes it at
 * verifyTwoStep.
 */
export interface TwoStepChallenge {
  requires_2fa: true;
  /** The method asked for first (the account's default). */
  channel: 'totp' | 'sms' | 'email';
  /** Every method that is on, so the code step can offer the others ("Text me a code instead"). */
  methods?: Array<'totp' | 'sms' | 'email'>;
  challenge: string;
  email: string;
  phone_hint: string | null;
  /** Set when the text/email could not be sent (rate limit, no phone): shown on the code step. */
  error?: string;
}

/** The methods the code step can switch between (an older gateway sends only `channel`). */
export function challengeMethods(c: Pick<TwoStepChallenge, 'channel' | 'methods'>): TwoStepChallenge['channel'][] {
  return c.methods?.length ? c.methods : [c.channel];
}

/** Wrong codes answer 401; this keeps the client from treating that as an expired session. */
const NO_SESSION_RETRY = { _retry: true } as AxiosRequestConfig;

export async function login(credentials: LoginCredentials): Promise<TwoStepChallenge | null> {
  const res = await apiClient.post<{ data: { ok?: boolean } | TwoStepChallenge }>('/auth/login/cookie', credentials);
  const data = res.data?.data;
  return data && 'requires_2fa' in data && data.requires_2fa ? data : null;
}

export async function verifyTwoStep(challenge: string, code: string): Promise<{ used_backup_code: boolean }> {
  const res = await apiClient.post<{ data: { ok: boolean; used_backup_code?: boolean } }>(
    '/auth/2fa/verify/cookie',
    { challenge, code },
    NO_SESSION_RETRY,
  );
  return { used_backup_code: !!res.data?.data?.used_backup_code };
}

/** Text or email the code again; `channel` sends it another way the account has on. */
export async function resendTwoStepCode(challenge: string, channel?: 'sms' | 'email'): Promise<void> {
  await apiClient.post('/auth/2fa/resend', channel ? { challenge, channel } : { challenge }, NO_SESSION_RETRY);
}

export async function register(data: RegisterData): Promise<void> {
  await apiClient.post('/auth/register/cookie', data);
}

export async function logout(): Promise<void> {
  try {
    await apiClient.post('/auth/logout/cookie');
  } catch {
    // Ignore errors on logout — cookies are cleared client-side regardless.
  }
}

/**
 * Public password-reset flow (no auth). Request emails a 6-digit code; reset
 * consumes it. The request call always succeeds server-side (no account enumeration).
 */
export async function passwordResetRequest(email: string): Promise<void> {
  await apiClient.post('/auth/password-reset-request', { email });
}

export async function passwordReset(email: string, code: string, password: string): Promise<void> {
  await apiClient.post('/auth/password-reset', { email, code, password });
}

/** Sign in with Google: shown only when the gateway has a Google client configured. */
export async function googleSignInEnabled(): Promise<boolean> {
  try {
    const res = await apiClient.get<{ data: { enabled: boolean } }>('/auth/google/status', NO_SESSION_RETRY);
    return !!res.data?.data?.enabled;
  } catch {
    return false;
  }
}

/** A full-page link (not an XHR): it goes to Google and comes back to the gateway. */
export const GOOGLE_SIGN_IN_URL = `${API_URL}/auth/google/start`;

/**
 * Back from Google on a two-step account: the gateway puts the challenge in the URL fragment
 * (`/login#two_step=…`), which never reaches a server. Returns it, or null.
 */
export function challengeFromHash(hash: string): TwoStepChallenge | null {
  const m = /(?:^#|&)two_step=([A-Za-z0-9_-]+)/.exec(hash);
  if (!m) return null;
  try {
    const b64 = m[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const parsed = JSON.parse(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '='))) as Partial<TwoStepChallenge>;
    if (parsed.requires_2fa !== true || typeof parsed.challenge !== 'string' || !parsed.channel) return null;
    return {
      requires_2fa: true,
      channel: parsed.channel,
      challenge: parsed.challenge,
      email: String(parsed.email ?? ''),
      phone_hint: parsed.phone_hint ?? null,
      ...(parsed.error ? { error: parsed.error } : {}),
    };
  } catch {
    return null;
  }
}
